import { useQuery } from '@tanstack/react-query';

import { listConnectionProviders } from '@/lib/api/generated/api';
import type { ConnectionProviderView } from '@/lib/api/generated/model';

import { connectionKeys } from '../queryKeys';

/**
 * The provider catalog (connections rework) — which providers can be connected,
 * their form fields, and the entries each creates. Backend-owned, so this app
 * carries no provider knowledge of its own. Static for a build, hence the long
 * `staleTime`.
 */
export function useConnectionCatalog(
  workspaceId: string,
  { enabled = true }: { enabled?: boolean } = {},
) {
  const query = useQuery({
    queryKey: connectionKeys.catalog(workspaceId),
    queryFn: async (): Promise<ConnectionProviderView[]> => {
      const response = await listConnectionProviders(workspaceId);
      if (response.status !== 200) {
        throw new Error('The connection catalog could not be loaded.');
      }
      return response.data;
    },
    staleTime: Infinity,
    enabled,
    retry: false,
  });

  // A multi-instance type (the container registry) has no managed token to
  // connect: it is listed on the Connections page but kept out of `providers`, which
  // the Connect sheet, the Secrets page and the in-place create all treat as
  // "connect once".
  return {
    providers: query.data?.filter((p) => !p.multi_instance),
    multiProviders: query.data?.filter((p) => p.multi_instance),
    isPending: query.isPending,
    loadFailed: query.isError,
  };
}
